import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { rulesApi } from '../services/api';
import { rulePath, titleToSlug } from '../utils/slug';
import RuleCard from '../components/RuleCard';
import SearchBox from '../components/SearchBox';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Loading from '../components/Loading';
import './Rules.css';

function Rules({ random = false, latest = false }) {
  const navigate = useNavigate();
  const { id: categoryId } = useParams(); // Get category ID from URL if /rules/category/:id
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryName, setCategoryName] = useState(null);

  const loadLatestRules = useCallback(async () => {
    try {
      setLoading(true);
      const response = await rulesApi.getLatest();
      setRules(response.data.data || []);
      setCategoryName(null);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCategoryRules = useCallback(async (catId) => {
    try {
      setLoading(true);
      const response = await rulesApi.getByCategory(catId);
      setRules(response.data.data || []);
      
      // Get category name from first rule or fetch categories
      if (response.data.data && response.data.data.length > 0) {
        setCategoryName(response.data.data[0].category_name || response.data.data[0].gr_other1);
      }
      
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load category rules');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRandomRule = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading random rule...'); // Debug
      const response = await rulesApi.getRandom();
      console.log('Random rule response:', response); // Debug
      const rule = response.data.data?.rule || response.data.data;
      if (rule) {
        const ruleId = rule.id || rule.gr_id;
        const slug = rule.slug || titleToSlug(rule.gr_desc || rule.description || '');
        navigate(rulePath(ruleId, slug));
      } else {
        setError('Random rule data not found');
        setLoading(false);
      }
    } catch (err) {
      console.error('Random rule error:', err); // Debug
      console.error('Error response:', err.response); // Debug
      console.error('Error message:', err.message); // Debug
      setError(
        err.response?.data?.message || 
        err.message || 
        'Failed to load random rule. Check your connection and try again.'
      );
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (random) {
      loadRandomRule();
    } else if (categoryId) {
      loadCategoryRules(categoryId);
    } else {
      loadLatestRules();
    }
  }, [random, categoryId, loadRandomRule, loadCategoryRules, loadLatestRules]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="container">
          <Loading message={random ? 'Loading random rule…' : 'Loading rules…'} />
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    const retry = random ? loadRandomRule : (categoryId ? () => loadCategoryRules(categoryId) : loadLatestRules);
    return (
      <>
        <Header />
        <div className="container">
          <div className="error-message">{error}</div>
          <button type="button" className="btn btn-primary mt-3" onClick={retry}>
            Try again
          </button>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="container">
        <div className="rules-page">
          <h1>{categoryName ? `Category: ${categoryName}` : 'GuyRules'}</h1>
          <SearchBox />
          
          <div className="rules-grid">
            {rules.length === 0 ? (
              <p>No rules found</p>
            ) : (
              rules.map((rule) => (
                <RuleCard key={rule.id || rule.gr_id} rule={rule} />
              ))
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default Rules;
